import { h, Component, Fragment } from 'preact';
import cookies from '@buzzfeed/bf-utils/lib/cookies';
import styles from './styles.module.scss';
import { I18n, Config } from '../context';
import { UseDefaultUserIcon } from '../svg/icons/default-user';
import { Link } from '../link';
import { Button } from '../button';
import { Notifications } from './notifications';
import { AllNotifications } from './notifications/all-notifications';
import { removeTeaserNotifications } from '../teaserNotifications';
import { UseCloseIcon } from '../svg/icons/close';
import { ChevronRightMiniIcon } from '../svg/icons/chevron-right-mini';
import { GiftBoxIcon } from '../svg/icons/gift-box';
import { withTheme } from '../withTheme';
import SignInPrompt from './sign-in-prompt';
import NotificationTooltip from '../notif-tooltip';

let FocusTrap;
if (typeof window !== 'undefined') {
  FocusTrap = require('focus-trap-react');
}
const userMenuId = 'js-user-menu';
const maxNotifsDisplayed = 10;
const notificationsPageSize = 20;
const supportedEditions = ['au', 'ca', 'in', 'us', 'uk'];
// same across every environment/edition, see config.yml `client` block
const USER_MENU_PATHS = {
  newPost: '/post',
  settings: '/settings',
  bookmarks: '/bookmarks',
  membership: '/member-center/me?utm_source=buzzfeed&utm_medium=web&utm_campaign=user_menu',
};

const submitLogoutForm = (bfUrl, xsrf) => {
  const form = document.createElement('form');
  form.method = 'post';
  form.action = `${bfUrl}/auth/signout`;
  form.style.display = 'none';
  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = '_xsrf';
  input.value = xsrf;
  form.appendChild(input);
  document.body.appendChild(form);
  form.submit();
};

const onLogout = (bfUrl) => () => {
  const xsrf = cookies.get('_xsrf');
  if (xsrf) {
    submitLogoutForm(bfUrl, xsrf);
    return;
  }
  // no xsrf cookie set yet; prime one before logging out
  fetch(`${bfUrl}/auth/signin`, { credentials: 'include' }).then(() => {
    const primedXsrf = cookies.get('_xsrf');
    if (primedXsrf) {
      submitLogoutForm(bfUrl, primedXsrf);
    }
  });
};

class Toggle extends Component {
  render({ children, theme, ...props }) {
    return (
      <Button
        {...props}
        className={`${styles.menuToggle} ${theme.menuToggle}`}
        aria-controls={userMenuId}
        location={'avatar'}
      >
        {children}
      </Button>
    );
  }
}

const ThemedToggle = withTheme(Toggle);

const Avatar = ({ displayName, image, username, theme, href, ...props }) => {
  let AvatarItself;

  if (image) {
    AvatarItself = (
      <Config.Consumer>
        {(config) => (
          <img
            className={`${styles.userAvatar} ${theme.userAvatar}`}
            src={config.image_service_url + image}
            alt={username}
            {...props}
          />
        )}
      </Config.Consumer>
    );
  } else {
    AvatarItself = (
      <UseDefaultUserIcon
        className={`${styles.userAvatar} ${theme.userAvatar}`}
        {...props}
      />
    );
  }

  return (
    <div className={styles.userAvatarContainer}>
      {href && (
        <Link href={href} location={'userMenu'} label={displayName}>
          {AvatarItself}
        </Link>
      )}
      {!href && AvatarItself}
    </div>
  );
};

const ThemedAvatar = withTheme(Avatar);

const UserOptions = ({
  notifMenuEnabled,
  userOptionsEnabled,
  signInPromptEnabled,
  isEditionSupported,
  userInfo,
  email,
  isEmailLoaded,
  showNotifs,
  showMembershipUpsell,
  onUserMenuClicked,
  notifications,
  totalUnreadCount,
  onLoadMoreNotifications,
  hasMoreNotifications,
  isLoadingMoreNotifications,
  isVisible,
  onMarkAsReadClicked,
  step,
  onShowAllNotifications,
  onGoBack,
}) => {
  const {
    image,
    displayName,
    username,
    isCommunityUser,
    isContributorUser,
    homeUrl,
    dashboardUrl,
  } = userInfo || {};

  const CloseButton = (
    <ThemedToggle
      onClick={onUserMenuClicked}
      aria-expanded={String(isVisible)}
      aria-label={'close'}
    >
      <UseCloseIcon width={22} height={22} aria-hidden={true} />
    </ThemedToggle>
  );
  return (
    <Config.Consumer>
      {(config) => (
        <div
          className={`${styles.userMenu} ${step === 'allNotifications' ? styles.userMenuFull : ''}`}
        >
          {step === 'allNotifications' ? (
            <AllNotifications
              notifications={notifications}
              totalUnreadCount={totalUnreadCount}
              onMarkAsReadClicked={onMarkAsReadClicked}
              onGoBack={onGoBack}
              onLoadMore={onLoadMoreNotifications}
              hasMore={hasMoreNotifications}
              isLoadingMore={isLoadingMoreNotifications}
            />
          ) : (
            <Fragment>
              <I18n.Consumer>
                {(i18n) => (
                  <Fragment>
                    {userOptionsEnabled && (
                      <Fragment>
                        <div className={styles.userOptionsSettings}>
                          <Link
                            href={USER_MENU_PATHS.settings}
                            location={'userMenu'}
                            label={'settings'}
                            className={styles.settingsBtn}
                          >
                            {i18n.settings}
                          </Link>
                        </div>
                        <div className={styles.userOptionsProfile}>
                          <div className={styles.userAvatarEditWrapper}>
                            <ThemedAvatar
                              className={`${styles.userAvatar}`}
                              image={typeof image === 'string' ? image.replace(/\.(\w+)$/, '_large.$1') : image}
                              username={username}
                              displayName={displayName}
                              href={config.bf_url + '/' + username}
                            />
                          </div>
                          <p className={styles.userProfileName}>
                            <Link
                              href={config.bf_url + '/' + username}
                              location={'userMenu'}
                              label={displayName}
                            >
                              {displayName}
                              {(!isEmailLoaded || email) && (
                                <span
                                  className={`${styles.userEmail} ${
                                    !isEmailLoaded
                                      ? styles.userEmailLoading
                                      : ''
                                  }`}
                                >
                                  {isEmailLoaded ? email : ''}
                                </span>
                              )}
                            </Link>
                          </p>
                        </div>
                        <div className={styles.userOptions}>
                          <Link
                            href={
                              isCommunityUser
                                ? `${config.bf_url}/quiz-cms`
                                : homeUrl + USER_MENU_PATHS.newPost
                            }
                            location={'userMenu'}
                            label={
                              isCommunityUser
                                ? 'make a quiz'
                                : 'create new post'
                            }
                            className={styles.makeBtn}
                          >
                            {isCommunityUser
                              ? 'Make A Quiz!'
                              : 'Create New Post'}
                          </Link>
                          <div className={styles.navCard}>
                            <Link
                              href={homeUrl}
                              location={'userMenu'}
                              label={
                                isCommunityUser
                                  ? 'community corner'
                                  : 'my posts'
                              }
                              className={styles.navItem}
                            >
                              <span>
                                {isCommunityUser
                                  ? 'Community Corner'
                                  : 'My Posts'}
                              </span>
                              <span className={styles.navItemRight}>
                                {isCommunityUser && (
                                  <span className={styles.newBadge}>New</span>
                                )}
                                <ChevronRightMiniIcon aria-hidden={true} />
                              </span>
                            </Link>
                            <Link
                              href={dashboardUrl}
                              location={'userMenu'}
                              label={'my dashboard'}
                              className={styles.navItem}
                            >
                              <span>My Dashboard</span>
                              <ChevronRightMiniIcon aria-hidden={true} />
                            </Link>
                            <Link
                              href={`${config.bf_url}/${username}${USER_MENU_PATHS.bookmarks}`}
                              location={'userMenu'}
                              label={'bookmarks'}
                              className={styles.navItem}
                            >
                              <span>Bookmarks</span>
                              <ChevronRightMiniIcon aria-hidden={true} />
                            </Link>
                            {(isCommunityUser || isContributorUser) && (
                              <Link
                                href={
                                  showMembershipUpsell
                                    ? `${config.bf_url}/member-center/signup/products`
                                    : USER_MENU_PATHS.membership
                                }
                                location={'userMenu'}
                                label={'membership'}
                                className={`${styles.navItem} ${styles.navItemLast}`}
                              >
                                <span>Membership</span>
                                <ChevronRightMiniIcon aria-hidden={true} />
                              </Link>
                            )}
                          </div>
                          {notifMenuEnabled && showNotifs && (
                            <Notifications
                              notifications={notifications}
                              totalUnreadCount={totalUnreadCount}
                              onMarkAsReadClicked={onMarkAsReadClicked}
                              onShowAllNotifications={onShowAllNotifications}
                            />
                          )}
                          {showMembershipUpsell && (
                            <Link
                              href={`${config.bf_url}/member-center/signup/products`}
                              location={'userMenu'}
                              label={'membership banner'}
                              className={styles.membershipBanner}
                            >
                              <span
                                className={styles.membershipBannerIcon}
                                aria-hidden="true"
                              >
                                <GiftBoxIcon />
                              </span>
                              <span className={styles.membershipBannerText}>
                                <span className={styles.membershipBannerTitle}>
                                  Get BuzzFeed Plus
                                </span>
                                <span
                                  className={styles.membershipBannerSubtitle}
                                >
                                  Keep BuzzFeed A Very Weird And Wonderful
                                  Place!
                                </span>
                              </span>
                            </Link>
                          )}
                          <div className={styles.navCard}>
                            <Button
                              onClick={onLogout(config.bf_url)}
                              location={'userMenu'}
                              label={'logout'}
                              className={styles.logoutButton}
                            >
                              <span>{i18n.logout}</span>
                              <ChevronRightMiniIcon aria-hidden={true} />
                            </Button>
                          </div>
                        </div>
                      </Fragment>
                    )}
                    {signInPromptEnabled && (
                      <SignInPrompt
                        closeButton={CloseButton}
                        showRandomLoginCta={isEditionSupported}
                      />
                    )}
                  </Fragment>
                )}
              </I18n.Consumer>
            </Fragment>
          )}
        </div>
      )}
    </Config.Consumer>
  );
};

/*
  NotifUserMenuContainer duplicates the logic found in the MoreNav component
  customized to pass more props into UserOptions component
*/
class NotifUserMenuContainer extends Component {
  render({
    isVisible,
    userInfo,
    onMarkAsReadClicked,
    notifMenuEnabled,
    userOptionsEnabled,
    signInPromptEnabled,
    isEditionSupported,
    showNotifs,
    showMembershipUpsell,
    email,
    isEmailLoaded,
    onUserMenuClicked,
    notifications,
    totalUnreadCount,
    onLoadMoreNotifications,
    hasMoreNotifications,
    isLoadingMoreNotifications,
    step,
    onShowAllNotifications,
    onGoBack,
  }) {
    return (
      <section
        className={`${styles.userMenuContainer}  ${
          this.props.isVisible ? styles.visible : ''
        }`}
        id={userMenuId}
      >
        {this.props.isVisible && (
          <FocusTrap focusTrapOptions={{ clickOutsideDeactivates: true }}>
            <UserOptions
              userInfo={userInfo}
              email={email}
              isEmailLoaded={isEmailLoaded}
              notifMenuEnabled={notifMenuEnabled}
              userOptionsEnabled={userOptionsEnabled}
              signInPromptEnabled={signInPromptEnabled}
              isEditionSupported={isEditionSupported}
              showNotifs={showNotifs}
              showMembershipUpsell={showMembershipUpsell}
              onUserMenuClicked={onUserMenuClicked}
              notifications={notifications}
              totalUnreadCount={totalUnreadCount}
              onLoadMoreNotifications={onLoadMoreNotifications}
              hasMoreNotifications={hasMoreNotifications}
              isLoadingMoreNotifications={isLoadingMoreNotifications}
              isVisible={isVisible}
              onMarkAsReadClicked={onMarkAsReadClicked}
              step={step}
              onShowAllNotifications={onShowAllNotifications}
              onGoBack={onGoBack}
            />
          </FocusTrap>
        )}
      </section>
    );
  }
}

class NotifUserMenu extends Component {
  constructor(props) {
    super(props);
    this.onMarkAsReadClicked = this.onMarkAsReadClicked.bind(this);
    this.onMarkTeaserAsReadClicked = this.onMarkTeaserAsReadClicked.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.longPollNotifications = this.longPollNotifications.bind(this);
    this.initNotifications = this.initNotifications.bind(this);
    this.loadMoreNotifications = this.loadMoreNotifications.bind(this);
    this.checkMembershipStatus = this.checkMembershipStatus.bind(this);
    this.fetchUserEmail = this.fetchUserEmail.bind(this);
    this.showAllNotifications = this.showAllNotifications.bind(this);
    this.goBack = this.goBack.bind(this);
    this.membershipStatusChecked = false;
    // host may supply email up front (e.g. from its own auth data), so only fetch it ourselves if missing
    this.emailFetched = !!(props.userInfo && typeof props.userInfo.email !== 'undefined');
    this.state = {
      enabled: supportedEditions.includes(this.props.edition),
      notifications: [],
      totalUnreadCount: 0,
      hasMoreNotifications: true,
      isLoadingMoreNotifications: false,
      authenticated: true,
      pageVisible: true,
      documentTitle: '',
      showMembershipUpsell: false,
      email: this.emailFetched ? props.userInfo.email : null,
      isEmailLoaded: this.emailFetched,
      step: 'main',
    };
  }

  componentDidUpdate(prevProps) {
    if (
      !this.membershipStatusChecked &&
      this.isEligibleForMembershipUpsell() &&
      !prevProps.isVisible &&
      this.props.isVisible
    ) {
      this.membershipStatusChecked = true;
      this.checkMembershipStatus();
    }
    if (!this.emailFetched && !prevProps.isVisible && this.props.isVisible) {
      this.emailFetched = true;
      this.fetchUserEmail();
    }
    // reset step when panel closes so reopening always starts at main
    if (prevProps.isVisible === true && !this.props.isVisible) {
      this.setState({ step: 'main' });
    }
    if (!this.state.enabled || !this.props.userInfo) {
      return;
    }
    if (!prevProps.userInfo) {
      this.initNotifications();
    } else if (prevProps.isVisible === false && this.props.isVisible === true) {
      this.updateNotifications();
    }
    const unreadLength = this.state.totalUnreadCount;
    if (unreadLength > 0) {
      document.title = `(${unreadLength}) ${this.state.documentTitle}`;
      // otherwise if its 0, whether it matches or not, set the title
    } else {
      document.title = this.state.documentTitle;
    }
  }

  componentDidMount() {
    const { teaserNotifications } = this.props;

    if (
      teaserNotifications &&
      teaserNotifications.length > 0 &&
      !this.props.userInfo
    ) {
      this.setState({
        notifications: teaserNotifications.map((n) => ({ ...n, read: false })),
        totalUnreadCount: teaserNotifications.length,
        hasMoreNotifications: false,
      });
      return;
    }

    this.setState({
      documentTitle: document.title,
    });

    if (this.state.enabled && this.props.userInfo) {
      this.initNotifications();
    }
  }

  // banner only ever applies to community/contributor users
  isEligibleForMembershipUpsell() {
    const { userInfo } = this.props;
    return (
      !!userInfo && (userInfo.isCommunityUser || userInfo.isContributorUser)
    );
  }

  // a user is subscribed only if an active buzzfeed_plus membership is found;
  // errors or unexpected responses default to showing the upsell banner
  checkMembershipStatus() {
    return fetch(`${this.props.bfURL}/member-center/v1/my-memberships`, {
      credentials: 'include',
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const memberships = Array.isArray(data?.items) ? data.items : [];
        const now = new Date();
        const isSubscribed = memberships.some((member) => {
          const membership = member.memberships?.[0];
          if (membership?.membership_type?.name !== 'buzzfeed_plus') {
            return false;
          }
          const startDate = new Date(membership.start_date);
          const endDate = new Date(membership.end_date);
          return startDate <= now && now < endDate;
        });
        this.setState({ showMembershipUpsell: !isSubscribed });
      })
      .catch(() => {
        this.setState({ showMembershipUpsell: true });
      });
  }

  // fetches the account email shown in the profile section; failures leave it blank
  fetchUserEmail() {
    return fetch(`${this.props.bfURL}/user-api/settings`, {
      credentials: 'include',
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        this.setState({ email: data?.email || null, isEmailLoaded: true });
      })
      .catch(() => {
        this.setState({ isEmailLoaded: true });
      });
  }

  initNotifications() {
    this.updateNotifications();
    this.setupPageVisibilityListener();
    this.timer = setInterval(this.longPollNotifications, 60 * 1000);
  }

  setupPageVisibilityListener() {
    let hidden, visibilityChange;
    if (typeof document.hidden !== 'undefined') {
      hidden = 'hidden';
      visibilityChange = 'visibilitychange';
    } else if (typeof document.msHidden !== 'undefined') {
      hidden = 'msHidden';
      visibilityChange = 'msvisibilitychange';
    } else if (typeof document.webkitHidden !== 'undefined') {
      hidden = 'webkitHidden';
      visibilityChange = 'webkitvisibilitychange';
    }

    // not render-affecting, so kept on the instance rather than in this.state
    this.hiddenPropName = hidden;
    this.removeVisibilityListener = () =>
      document.removeEventListener(
        visibilityChange,
        this.handleVisibilityChange,
      );

    document.addEventListener(
      visibilityChange,
      this.handleVisibilityChange,
      false,
    );
  }

  async fetchNotifications(userID, { pageSize, startId } = {}) {
    const params = new URLSearchParams({
      user_id: userID,
      enrich_meta: 'true',
    });
    if (pageSize) {
      params.set('page_size', pageSize);
    }
    if (startId) {
      params.set('start_id', startId);
    }
    return fetch(
      `${this.props.bfURL}/notification-api/v1/notifications?${params.toString()}`,
      { credentials: 'include' },
    ).then((response) => {
      // on an error code, stop polling and hide the menu.
      //
      // We don't modify any cookies, they probably do have to re-auth though
      // to reach a login state again. If we reach this state then it means
      // they have an invalid `bfauth_session` token, but their
      // `bf2-b_info` cookie persists which the front end uses.
      //
      // This could be made more destructive in that we could actually
      // trigger some cookie deletion so the user is made more aware that
      // they are logged out. That would also benefit our metrics
      if (response.status >= 400 && response.status < 500) {
        this.setState({ authenticated: false });
        return null;
      }

      this.setState({ authenticated: true });

      // 304 means nothing has changed, the server will determine this from
      // the ETag we've passed in
      if (response.status === 304) {
        return null;
      }

      return response.json();
    });
  }

  handleVisibilityChange() {
    if (document[this.hiddenPropName]) {
      this.setState({ pageVisible: false });
    } else {
      this.setState({ pageVisible: true });
    }
  }

  longPollNotifications() {
    if (this.state.authenticated && this.state.pageVisible) {
      this.updateNotifications();
    }
  }

  componentWillUnmount() {
    clearInterval(this.timer);
    if (this.removeVisibilityListener) {
      this.removeVisibilityListener();
    }
  }

  updateNotifications() {
    // avoid clobbering the paginated 'See All' list with the small preview page while it's open
    if (!this.props.userInfo || this.state.step === 'allNotifications') {
      return;
    }

    this.fetchNotifications(this.props.userInfo.id, {
      pageSize: maxNotifsDisplayed,
    })
      .then((response) => {
        if (!response || !response.results) {
          return;
        }
        // The API already returns unread and read notifications merged together
        const notifications = response.results
          .slice(0, maxNotifsDisplayed)
          .map((n) => ({ ...n, read: !!n.read_at }));
        this.setState({
          notifications,
          totalUnreadCount: response.unread_count || 0,
          hasMoreNotifications: response.results.length >= maxNotifsDisplayed,
        });
      })
      .catch((err) => console.error(err));
  }

  // fetches and appends the next page of notifications for the "See All" view
  loadMoreNotifications() {
    const { userInfo } = this.props;
    const { notifications, hasMoreNotifications, isLoadingMoreNotifications } =
      this.state;
    const lastNotification = notifications[notifications.length - 1];
    if (
      !userInfo ||
      !hasMoreNotifications ||
      isLoadingMoreNotifications ||
      !lastNotification
    ) {
      return;
    }

    this.setState({ isLoadingMoreNotifications: true });

    this.fetchNotifications(userInfo.id, {
      pageSize: notificationsPageSize,
      startId: lastNotification.id,
    })
      .then((response) => {
        if (!response || !response.results) {
          this.setState({ isLoadingMoreNotifications: false });
          return;
        }
        const nextNotifications = response.results.map((n) => ({
          ...n,
          read: !!n.read_at,
        }));
        this.setState({
          notifications: [...this.state.notifications, ...nextNotifications],
          hasMoreNotifications:
            nextNotifications.length >= notificationsPageSize,
          isLoadingMoreNotifications: false,
        });
      })
      .catch((err) => {
        console.error(err);
        this.setState({ isLoadingMoreNotifications: false });
      });
  }

  // this function marks either single or all notifications as read
  onMarkAsReadClicked(notification, markPreviousRead = false) {
    const { id } = notification;
    return fetch(
      `${this.props.bfURL}/notification-api/v1/notifications/${notification.id}`,
      {
        method: 'PATCH',
        body: JSON.stringify({
          read: true,
          mark_previous_read: markPreviousRead,
        }),
        credentials: 'include',
      },
    )
      .then((response) => response.json())
      .then((response) => {
        if (response.success) {
          if (markPreviousRead) {
            this.updateMarkAllAsReadState();
          } else {
            this.updateMarkAsReadState(id);
          }
        } else {
          return;
        }
      })
      .catch((error) => {
        console.log(error);
      });
  }

  onMarkTeaserAsReadClicked(_, markPreviousRead = false) {
    if (markPreviousRead) {
      removeTeaserNotifications();
      this.updateMarkAllAsReadState();
    }
  }

  // update state to reflect all notifications as read
  updateMarkAllAsReadState() {
    this.setState({
      notifications: this.state.notifications.map((notif) => ({
        ...notif,
        read: true,
      })),
      totalUnreadCount: 0,
    });
  }

  // update state to reflect one notification as read
  updateMarkAsReadState(id) {
    this.setState({
      notifications: this.state.notifications.map((notif) =>
        notif.id === id ? { ...notif, read: true } : notif,
      ),
      totalUnreadCount: Math.max(this.state.totalUnreadCount - 1, 0),
    });
  }

  showAllNotifications() {
    this.setState({ step: 'allNotifications' });
  }

  goBack() {
    this.setState({ step: 'main' });
  }

  render({
    notifMenuEnabled,
    userOptionsEnabled,
    signInPromptEnabled,
    teaserNotifications,
    isVisible,
    userInfo,
    onUserMenuClicked,
    onNotifTooltipClicked,
    edition,
    tooltipDismissed,
  }) {
    const isEditionSupported = supportedEditions.includes(edition);
    const isLoggedInUser = !!userInfo;
    const isNewOrCommunityUser =
      userInfo === null || (userInfo && userInfo.isCommunityUser);
    return (
      <div>
        <ThemedToggle
          onClick={onUserMenuClicked}
          aria-expanded={String(isVisible)}
          aria-label={'avatar'}
        >
          <NotificationTooltip
            onNotifTooltipClicked={onNotifTooltipClicked}
            tooltipDismissed={tooltipDismissed}
            teaserNotifications={teaserNotifications}
            isNewOrCommunityUser={isNewOrCommunityUser}
            isLoggedInUser={isLoggedInUser}
          >
            {!!this.state.totalUnreadCount && <div className={styles.blink} />}
            {isLoggedInUser && (
              <ThemedAvatar
                image={userInfo && userInfo.image}
                username={userInfo && userInfo.username}
              />
            )}
            {!isLoggedInUser && <div className={styles.signIn}>Sign In</div>}
          </NotificationTooltip>
        </ThemedToggle>
        <NotifUserMenuContainer
          bfURL={this.props.bfURL}
          edition={edition}
          isVisible={isVisible}
          userInfo={userInfo}
          onMarkAsReadClicked={
            userInfo ? this.onMarkAsReadClicked : this.onMarkTeaserAsReadClicked
          }
          onUserMenuClicked={onUserMenuClicked}
          notifications={this.state.notifications}
          totalUnreadCount={this.state.totalUnreadCount}
          onLoadMoreNotifications={this.loadMoreNotifications}
          hasMoreNotifications={this.state.hasMoreNotifications}
          isLoadingMoreNotifications={this.state.isLoadingMoreNotifications}
          teaserNotifications={teaserNotifications}
          notifMenuEnabled={notifMenuEnabled}
          userOptionsEnabled={userOptionsEnabled}
          signInPromptEnabled={signInPromptEnabled}
          showMembershipUpsell={this.state.showMembershipUpsell}
          showNotifs={this.state.enabled}
          isEditionSupported={isEditionSupported}
          email={this.state.email}
          isEmailLoaded={this.state.isEmailLoaded}
          step={this.state.step}
          onShowAllNotifications={this.showAllNotifications}
          onGoBack={this.goBack}
        />
      </div>
    );
  }
}

export default withTheme(NotifUserMenu);
